import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LayoutDashboard, Wand2, Images, ShoppingCart, Coins, Bell, User, Users, Receipt, Package, Cpu, Landmark, CreditCard, Clapperboard, MessageCircle, Settings, FileText, ScrollText } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/app/AppShell";
import { CookieConsent } from "@/components/app/PublicLayout";
import { Spinner } from "@/components/app/common";
import Landing from "@/pages/Landing";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import { LegalPage, ContactPage } from "@/pages/Legal";
import Activation from "@/pages/Activation";
import PaymentResult from "@/pages/PaymentResult";
import OrderDetail from "@/pages/OrderDetail";
import Dashboard from "@/pages/panel/Dashboard";
import Create from "@/pages/panel/Create";
import Gallery from "@/pages/panel/Gallery";
import Purchase from "@/pages/panel/Purchase";
import Transactions from "@/pages/panel/Transactions";
import { Notifications, Profile } from "@/pages/panel/Account";

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/admin/AdminUserDetail"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminCatalog = lazy(() => import("@/pages/admin/AdminCatalog"));
const AdminIban = lazy(() => import("@/pages/admin/AdminIban"));
const AdminPaytr = lazy(() => import("@/pages/admin/AdminPaytr"));
const AdminVideoRequests = lazy(() => import("@/pages/admin/AdminVideoRequests"));
const AdminWhatsApp = lazy(() => import("@/pages/admin/AdminWhatsApp"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

const USER_NAV = [
  { to: "/panel", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/panel/olustur", label: "İçerik Oluştur", icon: Wand2 },
  { to: "/panel/galeri", label: "Galerim", icon: Images },
  { to: "/panel/satin-al", label: "Satın Al", icon: ShoppingCart },
  { to: "/panel/krediler", label: "Kredi Hareketleri", icon: Coins },
  { to: "/panel/bildirimler", label: "Bildirim Ayarları", icon: Bell },
  { to: "/panel/profil", label: "Profil", icon: User },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Genel Bakış", icon: LayoutDashboard, end: true },
  { to: "/admin/kullanicilar", label: "Kullanıcılar", icon: Users },
  { to: "/admin/siparisler", label: "Siparişler & Ödemeler", icon: Receipt },
  { to: "/admin/video-talepleri", label: "Video Talepleri", icon: Clapperboard },
  { to: "/admin/urunler", label: "Paketler & AI Maliyetleri", icon: Package },
  { to: "/admin/iban", label: "IBAN / Havale Ayarları", icon: Landmark },
  { to: "/admin/paytr", label: "PayTR Ayarları", icon: CreditCard },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/admin/ayarlar", label: "Sistem Ayarları", icon: Settings },
  { to: "/admin/yasal", label: "Yasal Sayfalar", icon: FileText },
  { to: "/admin/denetim", label: "Denetim Kayıtları", icon: ScrollText },
];

const Guard = ({ children, role, active }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/giris" state={{ from: loc.pathname }} replace />;
  if (role && user.role !== role) return <Navigate to="/panel" replace />;
  if (active && user.status !== "ACTIVE") return <Navigate to="/aktivasyon" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/giris" element={<Login />} />
              <Route path="/kayit" element={<Register />} />
              <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
              <Route path="/sifre-sifirla" element={<ResetPassword />} />
              <Route path="/yasal/:slug" element={<LegalPage />} />
              <Route path="/iletisim" element={<ContactPage />} />
              <Route path="/aktivasyon" element={<Guard><Activation /></Guard>} />
              <Route path="/siparis/:id" element={<Guard><OrderDetail /></Guard>} />
              <Route path="/odeme/sonuc" element={<Guard><PaymentResult /></Guard>} />
              <Route path="/panel" element={<Guard active><AppShell items={USER_NAV} showCredits /></Guard>}>
                <Route index element={<Dashboard />} />
                <Route path="olustur" element={<Create />} />
                <Route path="galeri" element={<Gallery />} />
                <Route path="satin-al" element={<Purchase />} />
                <Route path="krediler" element={<Transactions />} />
                <Route path="bildirimler" element={<Notifications />} />
                <Route path="profil" element={<Profile />} />
              </Route>
              <Route path="/admin" element={<Guard role="ADMIN"><AppShell items={ADMIN_NAV} badge="Admin" /></Guard>}>
                <Route index element={<AdminDashboard />} />
                <Route path="kullanicilar" element={<AdminUsers />} />
                <Route path="kullanicilar/:id" element={<AdminUserDetail />} />
                <Route path="siparisler" element={<AdminOrders />} />
                <Route path="urunler" element={<AdminCatalog />} />
                <Route path="iban" element={<AdminIban />} />
                <Route path="paytr" element={<AdminPaytr />} />
                <Route path="video-talepleri" element={<AdminVideoRequests />} />
                <Route path="whatsapp" element={<AdminWhatsApp />} />
                <Route path="ayarlar" element={<AdminSettings section="general" />} />
                <Route path="yasal" element={<AdminSettings section="legal" />} />
                <Route path="denetim" element={<AdminSettings section="audit" />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
